import React, { useEffect, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'translate';
// ——————————————————————————————————————————————————
// TextScramble
// ——————————————————————————————————————————————————

class TextScramble {
  constructor(el) {
    this.el = el;
    this.chars = '&+%£@§$!<>-_\\/[]{}—=+*^?#________';
    this.update = this.update.bind(this);
  }
  setText(newText) {
    const oldText = this.el.innerText;
    const length = Math.max(oldText.length, newText.length);
    const promise = new Promise(resolve => {
      this.resolve = resolve;
    });
    this.queue = [];
    for (let i = 0; i < length; i += 1) {
      const from = oldText[i] || '';
      const to = newText[i] || '';
      const start = Math.floor(Math.random() * 10);
      const end = start + Math.floor(Math.random() * 400);
      this.queue.push({
        from, to, start, end,
      });
    }
    cancelAnimationFrame(this.frameRequest);
    this.frame = 0;
    this.update();
    return promise;
  }
  update() {
    let output = '';
    let complete = 0;
    for (let i = 0, n = this.queue.length; i < n; i += 1) {
      const {
        from, to, start, end,
      } = this.queue[i];
      let { char } = this.queue[i];
      if (this.frame >= end) {
        complete += 1;
        output += to;
      } else if (this.frame >= start) {
        if (!char || Math.random() < 0.05) {
          char = this.randomChar();
          this.queue[i].char = char;
        }
        output += `<span class="dud">${char}</span>`;
      } else {
        output += from;
      }
    }
    this.el.innerHTML = output;
    if (complete === this.queue.length) {
      this.resolve();
    } else {
      this.frameRequest = requestAnimationFrame(this.update);
      this.frame += 1;
    }
  }
  randomChar() {
    return this.chars[Math.floor(Math.random() * this.chars.length)];
  }
}

const ScrambledTextStyled = styled.div`
  margin: 10px 0;
  .ScrambledText__title {
    color: #17936F;
    font-weight: 500;
    font-size: 14px;
    line-height: 17px;
    text-align: center;
  }
  .ScrambledText__loader {
    margin-top: 10px;
    font-size: 16px;
    line-height: 17px;
    color: #555;
    text-align: center;
    font-weight: 400;
  }
`;

const ScrambledText = ({
  phrases, isLooped, onAnimationCompleted, shouldAnimate,
}) => {
  const containerRef = useRef(null);
  useEffect(() => {
    let isComponentMounted = true;
    let timerId;
    if (containerRef.current && shouldAnimate) {
      const fx = new TextScramble(containerRef.current);
      let counter = 0;
      const next = () => {
        fx.setText(phrases[counter]).then(() => {
          timerId = setTimeout(() => {
            if (!isComponentMounted) {
              return;
            }
            if (isLooped) {
              next();
            } else if (typeof onAnimationCompleted === 'function') {
              onAnimationCompleted(true);
            }
          }, 1000);
        });
        counter = (counter + 1) % phrases.length;
      };

      next();
    }
    return () => {
      clearTimeout(timerId);
      isComponentMounted = false;
    };
  }, []);
  return (
    <ScrambledTextStyled>
      <div className="ScrambledText__title">
        <span>
          <FormattedMessage id="campaign_action.loader_title" />
          ...
        </span>
      </div>
      <div className="ScrambledText__loader" ref={containerRef}>
        {!shouldAnimate && Array.isArray(phrases) && phrases[0]}
      </div>
    </ScrambledTextStyled>
  );
};

ScrambledText.defaultProps = {
  phrases: ['2key.io/QmPkdQbvf9CyYfe9w5jk3sZi6h'],
  isLooped: true,
  shouldAnimate: true,
};
ScrambledText.propTypes = {
  phrases: PropTypes.arrayOf(PropTypes.string),
  isLooped: PropTypes.bool,
  onAnimationCompleted: PropTypes.func,
  shouldAnimate: PropTypes.bool,
};

export default memo(ScrambledText);
